import React, { useRef } from 'react';
import { AppState, MovementPattern } from '../types';
import { Bluetooth, Play, Square, Activity, Settings2, Waves, RotateCw, Pause, Timer, Check, Anchor } from 'lucide-react';
import { WaterScene } from './WaterScene';
interface Props {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  onToggleFullScreen?: () => void;
  onProgramWithAI?: () => void;
  zoomLevel?: number;
}
const PATTERNS: {
  name: MovementPattern;
  desc: string;
}[] = [{
  name: 'Gentle Sway',
  desc: 'Smooth pendulum'
}, {
  name: 'Slow Sweep',
  desc: 'Wide slow arc'
}];
export function ControlPanel({
  state,
  setState,
  onToggleFullScreen,
  onProgramWithAI,
  zoomLevel = 1
}: Props) {
  const pauseSectionRef = useRef<HTMLDivElement>(null);
  const jigSectionRef = useRef<HTMLDivElement>(null);
  const updateState = (updates: Partial<AppState>) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
  };
  return <div className="h-full w-full px-3 pt-0 pb-0 flex flex-col bg-white text-gray-900 overflow-y-auto hud-scrollbar font-mono" data-id="element-183">
      <div className="flex-1 pb-4 relative z-20" data-id="element-184">
        {/* Armature Settings */}
        <div data-id="element-185">
          <div className="text-xs uppercase text-white ml-2 mb-0 tracking-widest font-extrabold flex items-center gap-2" data-id="element-186">
            <Settings2 size={12} data-id="element-187" /> Armature Settings
          </div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-188">
            {/* Armature Speed */}
            <div className="px-4 py-1 border-b border-[#00f0ff]/20" data-id="element-189">
              <div className="flex justify-between text-[12px] mb-0.5 uppercase tracking-wider" data-id="element-190">
                <span className="text-gray-900 font-bold" data-id="element-191">Armature Speed</span>
                <span className="text-white font-extrabold" data-id="element-192">
                  {state.speed.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-193">
                <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-194">
                  Slow
                </span>
                <input type="range" min="1" max="10" step="0.1" value={state.speed} onChange={e => updateState({
                speed: parseFloat(e.target.value)
              })} className="hud-slider" data-id="element-195" />
                <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-196">
                  Fast
                </span>
              </div>
            </div>

            {/* Swing Angle */}
            <div className="px-4 py-1 border-b border-[#00f0ff]/20" data-id="element-197">
              <div className="flex justify-between text-[12px] mb-0.5 uppercase tracking-wider" data-id="element-198">
                <span className="text-gray-900 font-bold flex items-center gap-2" data-id="element-199">
                  <RotateCw size={14} className="text-white" data-id="element-200" /> Swing Angle
                </span>
                <svg width="22" height="22" viewBox="0 0 36 36" className="shrink-0" data-id="element-201">
                  {/* Water line */}
                  <line x1="0" y1="14" x2="36" y2="14" stroke="#3b82f6" strokeWidth="0.5" opacity="0.4" data-id="element-202" />
                  {/* Bobber antenna/stem */}
                  <line x1="18" y1="2" x2="18" y2="8" stroke="#333" strokeWidth="1" strokeLinecap="round" data-id="element-203" />
                  {/* Antenna tip */}
                  <circle cx="18" cy="2" r="1" fill="#ff6600" data-id="element-204" />
                  {/* Bobber top half - red */}
                  <ellipse cx="18" cy="10" rx="4.5" ry="4" fill="#ff2222" stroke="#cc0000" strokeWidth="0.4" data-id="element-205" />
                  {/* Bobber bottom half - grey */}
                  <ellipse cx="18" cy="14" rx="4.5" ry="3" fill="#999999" stroke="#777777" strokeWidth="0.4" data-id="element-206" />
                  {/* White highlight on red */}
                  <ellipse cx="16.5" cy="9" rx="1.2" ry="1.8" fill="rgba(255,255,255,0.3)" data-id="element-207" />
                  {/* Armature lines showing swing angle */}
                  {(() => {
                  const angleRad = state.swingAngle * Math.PI / 180;
                  const armLen = 14;
                  const cx = 18,
                    cy = 17;
                  const lx = cx - armLen * Math.sin(angleRad);
                  const ly = cy + armLen * Math.cos(angleRad);
                  const rx = cx + armLen * Math.sin(angleRad);
                  const ry = cy + armLen * Math.cos(angleRad);
                  return <>
                        <line x1={cx} y1={cy} x2={lx} y2={ly} stroke="#333" strokeWidth="1" opacity="0.6" data-id="element-208" />
                        <line x1={cx} y1={cy} x2={rx} y2={ry} stroke="#333" strokeWidth="1" opacity="0.6" data-id="element-209" />
                        {/* Small lure dots at ends */}
                        <circle cx={lx} cy={ly} r="1.5" fill="#7fff00" stroke="#333" strokeWidth="0.3" data-id="element-210" />
                        <circle cx={rx} cy={ry} r="1.5" fill="#7fff00" stroke="#333" strokeWidth="0.3" data-id="element-211" />
                        {/* Dashed arc between */}
                        <line x1={lx} y1={ly} x2={rx} y2={ry} stroke="#3b82f6" strokeWidth="0.7" strokeDasharray="1.5 1.5" opacity="0.5" data-id="element-212" />
                      </>;
                })()}
                </svg>
              </div>
              <div className="flex items-center gap-2" data-id="element-213">
                <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-214">
                  Narrow
                </span>
                <input type="range" min="10" max="158" step="1" value={state.swingAngle} onChange={e => updateState({
                swingAngle: parseInt(e.target.value)
              })} className="hud-slider" data-id="element-215" />
                <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-216">
                  Wide
                </span>
              </div>

              {/* Sweep Style — Smooth to Jerky slider */}
              <div className="mt-1 pt-1 border-t border-[#00f0ff]/10" data-id="element-217">
                <div className="flex justify-between text-[11px] mb-0.5 uppercase tracking-wider" data-id="element-218">
                  <span className="text-gray-600 font-bold" data-id="element-219">Sweep Style</span>
                  <span className="text-white font-extrabold text-xs" data-id="element-220">
                    {state.sweepJerkiness === 0 ? 'Smooth' : state.sweepJerkiness <= 3 ? 'Slight' : state.sweepJerkiness <= 6 ? 'Moderate' : 'Jerky'}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-221">
                  <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-222">
                    Smooth
                  </span>
                  <input type="range" min="0" max="10" step="1" value={state.sweepJerkiness} onChange={e => updateState({
                  sweepJerkiness: parseInt(e.target.value)
                })} className="hud-slider" data-id="element-223" />
                  <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-224">
                    Jerky
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pause and Jig Controls */}
        <div ref={pauseSectionRef} data-id="element-225">
          <div className="text-xs uppercase text-white ml-2 mb-0 tracking-widest font-extrabold flex items-center gap-2" data-id="element-226">
            <Pause size={12} data-id="element-227" /> Pause & Jig
          </div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-228">
            {/* Pause Toggle */}
            <div className={`flex items-center justify-between px-4 py-1.5 ${state.pauseAtTop ? 'border-b border-[#00f0ff]/20' : 'border-b border-gray-100'}`} data-id="element-229">
              <span className="text-[11px] text-gray-900 font-bold uppercase tracking-wider whitespace-nowrap" data-id="element-230">
                Pause at Top of Swing
              </span>
              <button onClick={() => {
              const newVal = !state.pauseAtTop;
              updateState({
                pauseAtTop: newVal,
                ...(newVal ? {
                  pauseAngle: state.swingAngle,
                  pauseDuration: 0.5
                } : {})
              });
              if (newVal && pauseSectionRef.current) {
                setTimeout(() => {
                  pauseSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 50);
              }
              if (!newVal && pauseSectionRef.current) {
                const scrollContainer = pauseSectionRef.current.closest('.overflow-y-auto');
                if (scrollContainer) {
                  setTimeout(() => {
                    scrollContainer.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    });
                  }, 50);
                }
              }
            }} className={`w-[40px] h-[16px] relative flex items-center transition-colors duration-200 focus:outline-none rounded-full ${state.pauseAtTop ? 'bg-[#00ff88]' : 'bg-gray-300'}`} data-id="element-231">
                <div className={`w-[14px] h-[14px] absolute top-[1px] transition-all duration-200 rounded-full bg-white shadow-md ${state.pauseAtTop ? 'left-[24px]' : 'left-[2px]'}`} data-id="element-232" />
              </button>
            </div>

            {/* Pause Angle, Duration & Visual */}
            {state.pauseAtTop && <div className="px-4 py-2 animate-in fade-in slide-in-from-top-2 duration-200 space-y-2 border-b border-gray-100" data-id="element-233">
                {/* Pause Angle */}
                <div className="px-4 py-2 border-b border-[#00f0ff]/20" data-id="element-234">
                  <div className="flex justify-between text-[12px] mb-1.5 uppercase tracking-wider" data-id="element-235">
                    <span className="text-gray-900 font-bold flex items-center gap-2" data-id="element-236">
                      <RotateCw size={14} className="text-white" data-id="element-237" /> Swing Angle
                    </span>
                    <svg width="36" height="36" viewBox="0 0 36 36" className="shrink-0" data-id="element-238">
                      {/* Water line */}
                      <line x1="0" y1="14" x2="36" y2="14" stroke="#3b82f6" strokeWidth="0.5" opacity="0.4" data-id="element-239" />
                      {/* Bobber antenna/stem */}
                      <line x1="18" y1="2" x2="18" y2="8" stroke="#333" strokeWidth="1" strokeLinecap="round" data-id="element-240" />
                      {/* Antenna tip */}
                      <circle cx="18" cy="2" r="1" fill="#ff6600" data-id="element-241" />
                      {/* Bobber top half - red */}
                      <ellipse cx="18" cy="10" rx="4.5" ry="4" fill="#ff2222" stroke="#cc0000" strokeWidth="0.4" data-id="element-242" />
                      {/* Bobber bottom half - grey */}
                      <ellipse cx="18" cy="14" rx="4.5" ry="3" fill="#999999" stroke="#777777" strokeWidth="0.4" data-id="element-243" />
                      {/* White highlight on red */}
                      <ellipse cx="16.5" cy="9" rx="1.2" ry="1.8" fill="rgba(255,255,255,0.3)" data-id="element-244" />
                      {/* Armature lines showing swing angle */}
                      {(() => {
                    const angleRad = state.swingAngle * Math.PI / 180;
                    const armLen = 14;
                    const cx = 18,
                      cy = 17;
                    const lx = cx - armLen * Math.sin(angleRad);
                    const ly = cy + armLen * Math.cos(angleRad);
                    const rx = cx + armLen * Math.sin(angleRad);
                    const ry = cy + armLen * Math.cos(angleRad);
                    return <>
                            <line x1={cx} y1={cy} x2={lx} y2={ly} stroke="#333" strokeWidth="1" opacity="0.6" data-id="element-245" />
                            <line x1={cx} y1={cy} x2={rx} y2={ry} stroke="#333" strokeWidth="1" opacity="0.6" data-id="element-246" />
                            {/* Small lure dots at ends */}
                            <circle cx={lx} cy={ly} r="1.5" fill="#7fff00" stroke="#333" strokeWidth="0.3" data-id="element-247" />
                            <circle cx={rx} cy={ry} r="1.5" fill="#7fff00" stroke="#333" strokeWidth="0.3" data-id="element-248" />
                            {/* Dashed arc between */}
                            <line x1={lx} y1={ly} x2={rx} y2={ry} stroke="#1e40af" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.5" data-id="element-249" />
                          </>;
                  })()}
                    </svg>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-250">
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-251">
                      Narrow
                    </span>
                    <input type="range" min="10" max="170" step="1" value={state.swingAngle} onChange={e => {
                  const val = parseFloat(e.target.value);
                  updateState({
                    swingAngle: val,
                    pauseAngle: val
                  });
                }} className="hud-slider" data-id="element-252" />
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-253">
                      Wide
                    </span>
                  </div>
                </div>

                {/* Pause Duration Slider */}
                <div data-id="element-254">
                  <div className="flex justify-between text-[12px] mb-1 uppercase tracking-wider" data-id="element-255">
                    <span className="text-gray-900 font-bold flex items-center gap-2" data-id="element-256">
                      <Timer size={14} className="text-white" data-id="element-257" /> Pause Duration
                    </span>
                    <span className={`font-extrabold ${state.pauseCountdown > 0 ? 'text-[#00ff88] animate-pulse' : 'text-white'}`} data-id="element-258">
                      {state.pauseCountdown > 0 ? `⏱ ${state.pauseCountdown.toFixed(1)}s` : `${state.pauseDuration.toFixed(1)}s`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-259">
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-260">
                      Brief
                    </span>
                    <input type="range" min="0" max="60" step="0.5" value={state.pauseDuration} onChange={e => updateState({
                  pauseDuration: parseFloat(e.target.value)
                })} className="hud-slider" data-id="element-261" />
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-262">
                      Long
                    </span>
                  </div>
                </div>
              </div>}

            {/* Jig Toggle */}
            <div ref={jigSectionRef} className={`flex items-center justify-between px-4 py-1.5 ${state.jigAtPause ? 'border-b border-[#00f0ff]/20' : ''}`} data-id="element-263">
              <span className="text-[12px] text-gray-900 font-bold uppercase tracking-wider" data-id="element-264">
                Jig the lure
              </span>
              <button onClick={() => {
              const newVal = !state.jigAtPause;
              updateState({
                jigAtPause: newVal
              });
              if (newVal && jigSectionRef.current) {
                setTimeout(() => {
                  jigSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 50);
              }
            }} className={`w-[40px] h-[16px] relative flex items-center transition-colors duration-200 focus:outline-none rounded-full ${state.jigAtPause ? 'bg-[#00ff88]' : 'bg-gray-300'}`} data-id="element-265">
                <div className={`w-[14px] h-[14px] absolute top-[1px] transition-all duration-200 rounded-full bg-white shadow-md ${state.jigAtPause ? 'left-[24px]' : 'left-[2px]'}`} data-id="element-266" />
              </button>
            </div>

            {/* Jig Distance & Speed */}
            {state.jigAtPause && <div className="animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-267">
                {/* Jig Distance */}
                <div className="px-4 py-2 border-b border-[#00f0ff]/20" data-id="element-268">
                  <div className="flex justify-between text-[12px] mb-1 uppercase tracking-wider" data-id="element-269">
                    <span className="text-gray-900 font-bold" data-id="element-270">
                      Jig Distance
                    </span>
                  </div>
                  {/* Mini bobber jig visualization */}
                  <div className="flex justify-center mb-2" data-id="element-271">
                    <svg width="120" height="70" viewBox="0 0 120 70" data-id="element-272">
                      {/* Bobber body - realistic round shape */}
                      <ellipse cx="60" cy="15" rx="6" ry="9" fill="#ff4444" stroke="#cc0000" strokeWidth="0.5" data-id="element-273" />
                      {/* Bobber top cap */}
                      <ellipse cx="60" cy="7" rx="2.5" ry="2" fill="#ffffff" data-id="element-274" />
                      {/* Armature at rest (horizontal position) */}
                      {(() => {
                    const armLen = 30;
                    const restAngle = 90 * (Math.PI / 180); // horizontal
                    const restTipX = 60 + armLen * Math.sin(restAngle);
                    const restTipY = 28 + armLen * Math.cos(restAngle);
                    // Jigged position — rotates toward vertical based on jigDistance
                    const jigAngleOffset = state.jigDistance / 30 * 70; // up to 70° rotation
                    const jigAngle = (90 - jigAngleOffset) * (Math.PI / 180);
                    const jigTipX = 60 + armLen * Math.sin(jigAngle);
                    const jigTipY = 28 + armLen * Math.cos(jigAngle);
                    // Arc path for the sweep
                    const arcStartAngle = 90;
                    const arcEndAngle = 90 - jigAngleOffset;
                    const arcSteps = 20;
                    let arcPath = '';
                    for (let i = 0; i <= arcSteps; i++) {
                      const a = (arcStartAngle - (arcStartAngle - arcEndAngle) * (i / arcSteps)) * (Math.PI / 180);
                      const ax = 60 + armLen * Math.sin(a);
                      const ay = 28 + armLen * Math.cos(a);
                      arcPath += (i === 0 ? 'M' : 'L') + ` ${ax} ${ay}`;
                    }
                    return <>
                            {/* Rest armature - black */}
                            <line x1="60" y1="24" x2={restTipX} y2={restTipY} stroke="#000000" strokeWidth="1.5" opacity="0.3" data-id="element-275" />
                            {/* Jigged armature - black */}
                            <line x1="60" y1="24" x2={jigTipX} y2={jigTipY} stroke="#000000" strokeWidth="1.5" data-id="element-276" />
                            {/* Blue line between positions */}
                            <line x1={restTipX} y1={restTipY} x2={jigTipX} y2={jigTipY} stroke="#1e40af" strokeWidth="1" strokeDasharray="2 2" data-id="element-277" />
                            {/* Arrow showing jig direction */}
                            <line x1={jigTipX} y1={jigTipY - 2} x2={jigTipX} y2={jigTipY - 8} stroke="#1e40af" strokeWidth="1" data-id="element-278" />
                            <path d={`M ${jigTipX - 3} ${jigTipY - 5} L ${jigTipX} ${jigTipY - 9} L ${jigTipX + 3} ${jigTipY - 5}`} fill="none" stroke="#1e40af" strokeWidth="1" data-id="element-279" />
                          </>;
                  })()}
                      {/* Distance label */}
                      <text x="60" y="67" fill="#1e40af" fontSize="9" textAnchor="middle" fontFamily="monospace" fontWeight="bold" data-id="element-280">
                        {state.jigDistance}
                      </text>
                    </svg>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-281">
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-282">
                      Short
                    </span>
                    <input type="range" min="1" max="30" step="1" value={state.jigDistance} onChange={e => updateState({
                  jigDistance: parseInt(e.target.value)
                })} className="hud-slider" data-id="element-283" />
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-284">
                      Long
                    </span>
                  </div>
                </div>

                {/* Jig Speed Up */}
                <div className="px-4 py-2 border-b border-[#00f0ff]/20" data-id="element-285">
                  <div className="flex justify-between text-[12px] mb-1.5 uppercase tracking-wider" data-id="element-286">
                    <span className="text-gray-900 font-bold" data-id="element-287">Speed Up</span>
                    <span className="text-white font-extrabold" data-id="element-288">
                      {state.jigSpeedUp}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-289">
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-290">
                      Slow
                    </span>
                    <input type="range" min="1" max="10" step="1" value={state.jigSpeedUp} onChange={e => updateState({
                  jigSpeedUp: parseInt(e.target.value)
                })} className="hud-slider" data-id="element-291" />
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-292">
                      Fast
                    </span>
                  </div>
                </div>

                {/* Jig Speed Down */}
                <div className="px-4 py-2" data-id="element-293">
                  <div className="flex justify-between text-[12px] mb-1.5 uppercase tracking-wider" data-id="element-294">
                    <span className="text-gray-900 font-bold" data-id="element-295">Speed Down</span>
                    <span className="text-white font-extrabold" data-id="element-296">
                      {state.jigSpeedDown}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-297">
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-298">
                      Slow
                    </span>
                    <input type="range" min="1" max="10" step="1" value={state.jigSpeedDown} onChange={e => updateState({
                  jigSpeedDown: parseInt(e.target.value)
                })} className="hud-slider" data-id="element-299" />
                    <span className="text-[9px] text-[#5a7a8a] font-bold uppercase tracking-widest" data-id="element-300">
                      Fast
                    </span>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Full Screen Toggle Button - hidden on desktop */}
      {onToggleFullScreen && <div className="mx-2 mt-2 md:hidden" data-id="element-301">
          <button onClick={onToggleFullScreen} className="w-full py-2 font-extrabold text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] rounded-lg bg-[#1e40af] text-white" data-id="element-302">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" data-id="element-303">
              <path d="M1 5V1H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-304" />
              <path d="M15 11V15H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-305" />
              <path d="M15 5V1H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-306" />
              <path d="M1 11V15H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-307" />
            </svg>{' '}
            Show Bobber Full Screen
          </button>
        </div>}

      {/* Program Button */}
      {onProgramWithAI && <div className="mx-2 mt-2" data-id="element-308">
          <button onClick={onProgramWithAI} className="w-full py-1.5 font-extrabold text-[11px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] rounded-lg bg-[#1e40af] text-white hover:bg-[#2563eb]" data-id="element-309">
            Program with AI
          </button>
        </div>}

      {/* Action Button */}
      <div className="mt-auto pt-2 pb-2 px-2 relative z-20" data-id="element-310">
        <button onClick={() => updateState({
        isRunning: !state.isRunning
      })} className={`w-full py-1.5 font-extrabold text-[11px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] rounded-lg ${state.isRunning ? 'bg-red-500 text-white' : 'bg-[#1e40af] text-white'}`} data-id="element-311">
          {state.isRunning ? <>
              <Square size={14} fill="currentColor" data-id="element-312" /> Stop
            </> : <>
              <Play size={14} fill="currentColor" data-id="element-313" /> Start
            </>}
        </button>
      </div>
    </div>;
}